// @flow
import _ from 'lodash'
import type { Crop, FbPlacements, OptionType, UrlCheckResult } from '../../../components/types'
import type { PlacementType } from '../../../components/AudienceEdit/PlacementTypeSelect'

export type AudienceData = {|
  id: number,
  name: string,
  list: Array<string>,
  vkPlacement: string,
  placementType: PlacementType,
  audienceType: string,
  providersList: Array<string>
|}

type Errors = Array<string>

export type CommonFields = {|
  carouselStoreId: string | null,
  columnDescription: string,
  fbLinkButton: string | null,
  fbMultiDescription: string,
  fbTitle: string,
  fbUrl: string,
  id: ?number,
  logoImageCrop: Crop | null,
  logoImageStoreId: string | null,
  multiDescription: string,
  name: string,
  productDescription: string,
  productDescription: string,
  productTitle: string,
  snippetImageCrop: Crop | null,
  snippetImageStoreId: string | null,
  title: string,
  tmCarouselDescription: string,
  tmLinkButton: string | null,
  tmUrl: string,
  url: string,
  vkLinkButton: string | null,
  vkLinkTitle: string,
  vkMultiDescription: string,
  vkTitle: string,
  vkUrl: string,
  vkWithoutButtonDescription: string
|}

export type MediaState = {|
  columnImageStoreId: string | null,
  fbImageCrop: Crop | null,
  fbInstagramImageCrop: Crop | null,
  fbInstagramImageStoreId: string | null,
  fbStoriesImageCrop: Crop | null,
  fbStoriesImageStoreId: string | null,
  fbThumbnailImageCrop: Crop | null,
  fbThumbnailImageStoreId: string | null,
  glImageCrop: Crop | null,
  multiImageStoreId: string | null,
  tmColumnImageCrop: Crop | null,
  tmMultiImageCrop: Crop | null,
  tmSmallImageCrop: Crop | null,
  tmSmallImageStoreId: string | null,
  videoStoreId: string | null,
  vkArticleImageCrop: Crop | null,
  vkBigImageStoreId: string | null,
  vkColumnImageCrop: Crop | null,
  vkMultiImageCrop: Crop | null,
  vkSiteImageCrop: Crop | null
|}

export type FieldsState = {|
  common: CommonFields,
  media: Array<MediaState>
|}

export type CommonErrorsInfo<T> = {|
  columnDescriptionErrors: T,
  fbLinkButtonErrors: T,
  fbMultiDescriptionErrors: T,
  fbTitleErrors: T,
  fbUrlErrors: T,
  logoImageErrors: T,
  multiDescriptionErrors: T,
  productDescriptionErrors: T,
  productTitleErrors: T,
  snippetImageErrors: T,
  titleErrors: T,
  tmCarouselDescriptionErrors: T,
  tmLinkButtonErrors: T,
  tmUrlErrors: T,
  urlErrors: T,
  vkLinkButtonErrors: T,
  vkLinkTitleErrors: T,
  vkMultiDescriptionErrors: T,
  vkTitleErrors: T,
  vkUrlErrors: T,
  vkWithoutButtonDescriptionErrors: T
|}

export type CommonErrors = CommonErrorsInfo<Errors>

export type AdvertisementTemplateErrors = {|
  globalErrors: Errors,
  nameErrors: Errors
|}

export type MediaErrors = {|
  columnImageErrors: Errors,
  multiImageErrors: Errors,
  tmSmallImageErrors: Errors,
  fbInstagramImageErrors: Errors,
  fbStoriesImageErrors: Errors,
  videoErrors: Errors,
  fbThumbnailImageErrors: Errors,
  vkBigImageErrors: Errors
|}

export type ValidationState = {|
  commonErrors: CommonErrors,
  advertisementTemplateErrors: AdvertisementTemplateErrors,
  mediaErrors: Array<MediaErrors>
|}

export type AdvertisementTemplateAudienceData = {|
  id: number,
  name: string,
  list: Array<string>,
  vkPlacement: string,
  placementType: PlacementType,
  audienceType: string,
  providers: Array<string>,
  fbPlacements: FbPlacements
|}

export type ProcessState = {|
  isLoading: boolean,
  initialProviderTemplateNames: Array<string>,
  initialSelectedAudienceIds: Array<number>,
  audiences: Array<AdvertisementTemplateAudienceData>,
  urlCheck: ?UrlCheckResult,
  fbPromotionPosts: Array<OptionType>,
  editFormCopyPath: string
|}

export type State = {|
  ...ProcessState,
  ...ValidationState,
  ...FieldsState
|}

type PatchAction = {|
  type: 'AD_TEMPL.PATCH',
  state: $Shape<State>
|}

type MediaAddAction = {|
  type: 'AD_TEMPL.MEDIA_ADD'
|}

type MediaRemoveAction = {|
  type: 'AD_TEMPL.MEDIA_REMOVE',
  index: number
|}

type CommonPatchAction = {|
  type: 'AD_TEMPL.COMMON_PATCH',
  state: $Shape<CommonFields>
|}

type CommonErrorPatchAction = {|
  type: 'AD_TEMPL.COMMON_ERROR_PATCH',
  state: $Shape<CommonErrors>
|}

type AdvertisementTemplateErrorPatchAction = {|
  type: 'AD_TEMPL.AD_TPL_ERROR_PATCH',
  state: $Shape<AdvertisementTemplateErrors>
|}

type MediaPatchAction = {|
  type: 'AD_TEMPL.MEDIA_PATCH',
  state: $Shape<MediaState>,
  index: number
|}

type MediaErrorPatchAction = {|
  type: 'AD_TEMPL.MEDIA_ERROR_PATCH',
  state: $Shape<MediaErrors>,
  index: number
|}

type Action =
  | PatchAction
  | MediaAddAction
  | MediaRemoveAction
  | CommonPatchAction
  | CommonErrorPatchAction
  | MediaPatchAction
  | MediaErrorPatchAction
  | AdvertisementTemplateErrorPatchAction

export const patch = (diff: $Shape<State>): Action => ({
  type: 'AD_TEMPL.PATCH',
  state: diff
})

export const mediaAdd = (): Action => ({
  type: 'AD_TEMPL.MEDIA_ADD'
})

export const mediaRemove = (index: number): Action => ({
  type: 'AD_TEMPL.MEDIA_REMOVE',
  index
})

export const commonPatch = (diff: $Shape<CommonFields>): Action => ({
  type: 'AD_TEMPL.COMMON_PATCH',
  state: diff
})

export const commonErrorPatch = (diff: $Shape<CommonErrors>): Action => ({
  type: 'AD_TEMPL.COMMON_ERROR_PATCH',
  state: diff
})

export const advertisementTemplateErrorPatch = (
  diff: $Shape<AdvertisementTemplateErrors>
): Action => ({
  type: 'AD_TEMPL.AD_TPL_ERROR_PATCH',
  state: diff
})

export const mediaPatch = (index: number, diff: $Shape<MediaState>): Action => ({
  type: 'AD_TEMPL.MEDIA_PATCH',
  state: diff,
  index
})

export const mediaErrorPatch = (index: number, diff: $Shape<MediaErrors>): Action => ({
  type: 'AD_TEMPL.MEDIA_ERROR_PATCH',
  state: diff,
  index
})

export const emptyCommonErrors: CommonErrors = {
  urlErrors: [],
  vkUrlErrors: [],
  tmUrlErrors: [],
  fbUrlErrors: [],
  titleErrors: [],
  productTitleErrors: [],
  vkTitleErrors: [],
  fbTitleErrors: [],
  columnDescriptionErrors: [],
  productDescriptionErrors: [],
  multiDescriptionErrors: [],
  vkMultiDescriptionErrors: [],
  vkWithoutButtonDescriptionErrors: [],
  vkLinkTitleErrors: [],
  fbMultiDescriptionErrors: [],
  vkLinkButtonErrors: [],
  tmLinkButtonErrors: [],
  fbLinkButtonErrors: [],
  logoImageErrors: [],
  snippetImageErrors: [],
  tmCarouselDescriptionErrors: []
}

export const emptyAdvertisementTemplateErrors: AdvertisementTemplateErrors = {
  globalErrors: [],
  nameErrors: []
}

export const emptyMediaErrors: MediaErrors = {
  columnImageErrors: [],
  multiImageErrors: [],
  tmSmallImageErrors: [],
  fbInstagramImageErrors: [],
  fbStoriesImageErrors: [],
  videoErrors: [],
  fbThumbnailImageErrors: [],
  vkBigImageErrors: []
}

const emptyCommonFields: CommonFields = {
  carouselStoreId: null,
  columnDescription: '',
  fbLinkButton: null,
  fbMultiDescription: '',
  fbTitle: '',
  fbUrl: '',
  id: null,
  logoImageCrop: null,
  logoImageStoreId: null,
  multiDescription: '',
  name: '',
  productDescription: '',
  productTitle: '',
  snippetImageCrop: null,
  snippetImageStoreId: null,
  title: '',
  tmCarouselDescription: '',
  tmLinkButton: null,
  tmUrl: '',
  url: '',
  vkLinkButton: null,
  vkLinkTitle: '',
  vkMultiDescription: '',
  vkTitle: '',
  vkUrl: '',
  vkWithoutButtonDescription: ''
}

const emptyMediaFields: MediaState = {
  columnImageStoreId: null,
  fbImageCrop: null,
  fbInstagramImageCrop: null,
  fbInstagramImageStoreId: null,
  fbStoriesImageCrop: null,
  fbStoriesImageStoreId: null,
  fbThumbnailImageCrop: null,
  fbThumbnailImageStoreId: null,
  glImageCrop: null,
  multiImageStoreId: null,
  tmColumnImageCrop: null,
  tmMultiImageCrop: null,
  tmSmallImageCrop: null,
  tmSmallImageStoreId: null,
  videoStoreId: null,
  vkArticleImageCrop: null,
  vkBigImageStoreId: null,
  vkColumnImageCrop: null,
  vkMultiImageCrop: null,
  vkSiteImageCrop: null
}

const initialState: State = {
  isLoading: false,
  initialProviderTemplateNames: [],
  initialSelectedAudienceIds: [],
  audiences: [],
  urlCheck: null,
  fbPromotionPosts: [],
  common: emptyCommonFields,
  media: [emptyMediaFields],
  commonErrors: emptyCommonErrors,
  advertisementTemplateErrors: emptyAdvertisementTemplateErrors,
  mediaErrors: [emptyMediaErrors],
  editFormCopyPath: ''
}

function patchItem(state: State, index: number, transformItem: Function) {
  const item = state.media[index]
  const result = _.cloneDeep(state)

  result.media[index] = transformItem(item)

  return result
}

function patchItemError(state: State, index: number, transformItem: Function) {
  const item = state.mediaErrors[index]
  const result = _.cloneDeep(state)

  result.mediaErrors[index] = transformItem(item)

  return result
}

export default function advertisementTemplate(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'AD_TEMPL.PATCH':
      return { ...state, ...action.state }
    case 'AD_TEMPL.MEDIA_ADD':
      return {
        ...state,
        media: [...state.media, emptyMediaFields],
        mediaErrors: [...state.mediaErrors, emptyMediaErrors]
      }
    case 'AD_TEMPL.MEDIA_REMOVE': {
      let clonedState = _.cloneDeep(state)
      if (clonedState.media.length > 1) {
        clonedState.media.splice(action.index, 1)
      } else {
        clonedState.media = initialState.media
      }
      return clonedState
    }
    case 'AD_TEMPL.COMMON_PATCH': {
      const typedAction: CommonPatchAction = action
      return { ...state, common: { ...state.common, ...typedAction.state } }
    }
    case 'AD_TEMPL.COMMON_ERROR_PATCH': {
      const typedAction: CommonErrorPatchAction = action
      return { ...state, commonErrors: { ...state.commonErrors, ...typedAction.state } }
    }
    case 'AD_TEMPL.AD_TPL_ERROR_PATCH': {
      const typedAction: AdvertisementTemplateErrorPatchAction = action
      return {
        ...state,
        advertisementTemplateErrors: { ...state.advertisementTemplateErrors, ...typedAction.state }
      }
    }
    case 'AD_TEMPL.MEDIA_PATCH': {
      const typedAction: MediaPatchAction = action
      return patchItem(state, typedAction.index, (item) => ({ ...item, ...typedAction.state }))
    }
    case 'AD_TEMPL.MEDIA_ERROR_PATCH': {
      const typedAction: MediaErrorPatchAction = action
      return patchItemError(state, typedAction.index, (item) => ({ ...item, ...typedAction.state }))
    }
    default:
      return state
  }
}
